import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getLink, toast } from "../../../utils/helpers";
import { postRequest, request } from "../../../utils/request";
import dayjs from "dayjs";
import useFilters from "../../../hooks/useFilters";
import FiltersContainer from "../../general/FiltersContainer";
import Loader from "../../general/Loader";
import Pagination from "../../general/Pagination";
import Dropdown from "../../general/Dropdown";
import Modal from "../../general/Modal";
import ImportarCsv from "./ImportarCsv";
import ExportarInventario from "./ExportarInventario";

const canClose = (movimiento) => {
    for (const producto of movimiento.productos) {
        if (producto.cantidad === null || producto.cantidad === undefined || !producto.operacion) return false;
    }

    return true;
};

const InventarioMasivo = () => {
    const [movimientos, setMovimientos] = useState({});
    const [movimiento, setMovimiento] = useState();
    const [loading, setLoading] = useState(false);
    const [ejecutando, setEjecutando] = useState(false);
    const [showCloseModal, setShowCloseModal] = useState(false);
    const [showReopenModal, setShowReopenModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [pinSeguridad, setPinSeguridad] = useState("");
    const [diferencias, setDiferencias] = useState([]);
    const [verificando, setVerificando] = useState(false);
    const [showImportar, setShowImportar] = useState(false);
    const [showExportar, setShowExportar] = useState(false);
    const { handleFilterChange, filtersQuery } = useFilters({
        buscar: '', desde: null, hasta: dayjs().format("YYYY-MM-DD"),
    });

    const openCloseModal = async (movimiento) => {
        setMovimiento(movimiento);
        setDiferencias([]);
        setShowCloseModal(true);

        setVerificando(true);
        const response = await request(`movimientos/${movimiento.id}/verificar`);
        setDiferencias(response?.data ?? []);
        setVerificando(false);
    };

    const closeCloseModal = () => {
        setMovimiento(null);
        setDiferencias([]);
        setShowCloseModal(false);
    };

    const openReopenModal = (movimiento) => {
        setMovimiento(movimiento);
        setShowReopenModal(true);
    };

    const closeReopenModal = () => {
        setMovimiento(null);
        setShowReopenModal(false);
    };

    const openDeleteModal = (movimiento) => {
        setMovimiento(movimiento);
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setMovimiento(null);
        setShowDeleteModal(false);
    };

    const getMovimientos = async (url) => {
        url = url ? url + "&limit=15" : `movimientos?limit=15`;

        setLoading(true);

        const response = await request(url);

        setMovimientos(response);

        setLoading(false);
    };

    const cerrarMovimiento = async (recalcular) => {
        if (ejecutando) return;

        setEjecutando(true);

        const form = new FormData();
        form.append('recalcular', recalcular ? 1 : 0);
        const response = await postRequest(`movimientos/${movimiento.id}/cerrar`, form);

        if(response.success) {
            getMovimientos(`movimientos${filtersQuery}`);
            toast(`Se cerro el movimiento: '${movimiento.titulo}'`);
            closeCloseModal();
        } else {
            toast(response.data ?? "Error cerrando el movimiento.", 'error');
        }

        setEjecutando(false);
    };

    const reabrirMovimiento = async () => {
        if (!pinSeguridad) {
            toast("Pin de seguridad requerido", 'error');
            return;
        }

        if (ejecutando) return;

        setEjecutando(true);

        const form = new FormData();
        form.append('pin_seguridad', pinSeguridad);
        const response = await postRequest(`movimientos/${movimiento.id}/reabrir`, form);

        if(response.success) {
            getMovimientos(`movimientos${filtersQuery}`);
            toast(`El movimiento: '${movimiento.titulo}' reabierto`);
            closeReopenModal();
        } else {
            toast(response?.data?.message ? response.data.message : "Error cerrando el movimiento.", 'error');
        }

        setEjecutando(false);
    };

    const borrarMovimiento = async () => {
        if (!pinSeguridad) {
            toast("Pin de seguridad requerido", 'error');
            return;
        }

        if (ejecutando) return;

        setEjecutando(true);

        const form = new FormData();
        form.append('pin_seguridad', pinSeguridad);
        form.append('_method', 'delete');
        const response = await postRequest(`movimientos/${movimiento.id}`, form);

        if(response.success) {
            getMovimientos(`movimientos${filtersQuery}`);
            toast(`El movimiento: '${movimiento.titulo}' fue eliminado`);
            closeDeleteModal();
        } else {
            toast(response?.data?.message ? response.data.message : "Error eliminando el movimiento.", 'error');
        }

        setEjecutando(false);
    };

    useEffect(() => {
        getMovimientos(`movimientos${filtersQuery}`);
    }, [filtersQuery]);

    useEffect(() => setPinSeguridad(""), [showReopenModal, showDeleteModal]);

    return <>
        <div className='page-header'>
            <h1 className='page-title row'>
                <span className='col'>Movimientos de inventario masivos</span>

                <button
                    type='button'
                    className='au-btn au-btn-icon au-btn--blue au-btn--small mr-2'
                    onClick={() => setShowExportar(true)}>
                    <i className='zmdi zmdi-download' />
                    Exportar inventario
                </button>

                <button
                    type='button'
                    className='au-btn au-btn-icon au-btn--blue au-btn--small mr-2'
                    onClick={() => setShowImportar(true)}>
                    <i className='zmdi zmdi-upload' />
                    Importar Desde Archivo
                </button>

                <Link
                    className='au-btn au-btn-icon au-btn--green au-btn--small'
                    to={getLink(`/productos/inventario-masivo/create`)}>
                    <i className='zmdi zmdi-plus' />
                    NUEVO
                </Link>
            </h1>
        </div>

        <FiltersContainer>
            <div className='col'>
                <label htmlFor='search'>Buscar</label>
                <input
                    name='buscar'
                    onChange={(e) => handleFilterChange("buscar", e?.target.value)}
                    type='search'
                    placeholder='Buscar...'
                    className='form-control'
                />
            </div>

            <div className='col-2'>
                <label htmlFor='estado'>Estado</label>
                <select
                    name='estado'
                    onChange={(e) => handleFilterChange("estado", e.target.value)}
                    className='form-control'
                >
                    <option value="">Todos</option>
                    <option value="abierto">Abierto</option>
                    <option value="cerrado">Cerrado</option>
                </select>
            </div>

            <div className='col-2'>
                <label htmlFor='desde'>Desde</label>
                <input onChange={(e) => handleFilterChange("desde", e.target.value)}
                    type="date"
                    className='form-control'
                    name='desde'
                    id="desde"
                />
            </div>

            <div className='col-2'>
                <label htmlFor='hasta'>Hasta</label>
                <input onChange={(e) => handleFilterChange("hasta", e.target.value)}
                    type="date"
                    className='form-control'
                    name='hasta'
                    id="hasta"
                    defaultValue={dayjs().format('YYYY-MM-DD')}
                />
            </div>
        </FiltersContainer>

        <div className='row'>
            <div className='col-md-12'>
                <div>
                    <div className='table-data'>
                        <table className='table table-data2'>
                            <thead>
                                <tr>
                                    <th>CÓDIGO</th>
                                    <th>FECHA</th>
                                    <th>TITULO DEL INVENTARIO</th>
                                    <th>PRODUCTOS AFECTADOS</th>
                                    <th>USUARIO</th>
                                    <th>ESTADO DEL INVENTARIO</th>
                                    <th></th>
                                </tr>
                            </thead>

                            <tbody>
                                {loading
                                    ? (<tr className="tr-shadow"><td colSpan={14}><Loader /></td></tr>)
                                    : (movimientos?.data?.length
                                        ? movimientos.data.map((mov) => (
                                            <tr className="tr-shadow" key={mov.id}>
                                                <td>{mov.codigo}</td>
                                                <td>{dayjs(mov.fecha).format("DD/MM/YYYY")}</td>
                                                <td>{mov.titulo}</td>
                                                <td>{mov.productos.length}</td>
                                                <td>{mov.usuario.nombre}</td>

                                                <td style={{padding: '5px'}}>
                                                    { mov.estado === 'abierto' && <span className="badge badge-success p-2">ABIERTO</span> }
                                                    { mov.estado === 'cerrado' && <span className="badge badge-danger text-white p-2">CERRADO</span> }
                                                </td>
                                                <td style={{padding: '5px'}}>
                                                    <div className='table-data-feature'>
                                                        <Dropdown>
                                                            <Link
                                                                className='dropdown-item'
                                                                data-toggle='tooltip'
                                                                data-placement='top'
                                                                title=''
                                                                data-original-title='Print'
                                                                to={getLink(`/imprimir/inventario-masivo/${mov.id}`)}
                                                                target='_blank'>
                                                                Imprimir
                                                            </Link>

                                                            {mov.estado === 'cerrado' && <button
                                                                className='dropdown-item'
                                                                data-toggle='tooltip'
                                                                data-placement='top'
                                                                title=''
                                                                data-original-title='Edit'
                                                                onClick={() => openReopenModal(mov) }>
                                                                Re-Abrir
                                                            </button>}

                                                            {mov.estado !== 'cerrado' && <Link
                                                                className='dropdown-item'
                                                                data-toggle='tooltip'
                                                                data-placement='top'
                                                                title=''
                                                                data-original-title='Print'
                                                                to={getLink(`/productos/inventario-masivo/${mov.id}`)}>
                                                                Editar
                                                            </Link>}

                                                            {mov.estado !== 'cerrado' && canClose(mov) && <button
                                                                className='dropdown-item'
                                                                data-toggle='tooltip'
                                                                data-placement='top'
                                                                title=''
                                                                data-original-title='Close'
                                                                onClick={() => openCloseModal(mov) }>
                                                                Cerrar
                                                            </button>}

                                                            {mov.estado !== 'cerrado' && <button
                                                                className='dropdown-item'
                                                                data-toggle='tooltip'
                                                                data-placement='top'
                                                                title=''
                                                                data-original-title='Close'
                                                                onClick={() => openDeleteModal(mov) }>
                                                                Eliminar
                                                            </button>}
                                                        </Dropdown>
                                                    </div>
                                                </td>
                                            </tr>)
                                        )
                                        : (<tr className="tr-shadow"><td colSpan={12}>No hay ningún movimiento creado.</td></tr>)
                                    )
                                }
                            </tbody>
                        </table>

                        {movimientos?.links && (<Pagination links={movimientos.links} meta={movimientos.meta} getData={getMovimientos} />)}
                    </div>
                </div>
            </div>
        </div>

        <Modal
            show={showCloseModal}
            blank={false}
            toggle={closeCloseModal}
            acceptText="Cerrar Inventario"
            callback={() => cerrarMovimiento(false)}
            size={diferencias.length ? 'lg' : 'sm'}
            footer={verificando
                ? <Loader type="small" />
                : (diferencias.length > 0 && (ejecutando ? <Loader type="small" /> : <>
                    <button type="button" className="btn btn-plain" onClick={closeCloseModal}>Cancelar</button>
                    <button type="button" className="btn btn-outline-primary" onClick={() => cerrarMovimiento(false)}>Cerrar sin recalcular</button>
                    <button type="button" className="btn btn-primary" onClick={() => cerrarMovimiento(true)}>Recalcular y cerrar</button>
                </>))}
            saving={ejecutando}>
            <p>
                ¿Esta seguro que desea cerrar el movimiento de inventario masivo de titulo <strong>`{movimiento?.titulo}`</strong> de la fecha <strong>{dayjs(movimiento?.fecha).format('YYYY-MM-DD')}</strong>?
            </p>

            <div className="row mt-2">
                <small className="text-danger mx-auto">Al cerrar se afectara el inventario de los <strong>{movimiento?.productos?.length}</strong> productos presente.</small>
            </div>

            {diferencias.length > 0 && <div className="mt-3">
                <div className="alert alert-warning">
                    La existencia de <strong>{diferencias.length}</strong> productos cambió desde que se calculó el ajuste. Recalcular deja la existencia final igual al conteo (objetivo).
                </div>

                <table className="table table-sm">
                    <thead>
                        <tr>
                            <th>Producto</th>
                            <th>Existencia al importar</th>
                            <th>Existencia actual</th>
                            <th>Objetivo</th>
                            <th>Ajuste → Ajuste recalculado</th>
                        </tr>
                    </thead>
                    <tbody>
                        {diferencias.map((diferencia) => <tr key={diferencia.id}>
                            <td>{diferencia.referencia} - {diferencia.nombre}</td>
                            <td>{diferencia.existencia_referencia}</td>
                            <td>{diferencia.existencia_actual}</td>
                            <td>{diferencia.objetivo}</td>
                            <td>{diferencia.operacion} {diferencia.cantidad} → {diferencia.operacion_recalculada} {diferencia.cantidad_recalculada}</td>
                        </tr>)}
                    </tbody>
                </table>
            </div>}
        </Modal>

        <Modal
            show={showReopenModal}
            blank={false}
            toggle={closeReopenModal}
            acceptText="Reabrir Inventario"
            callback={reabrirMovimiento}
            saving={ejecutando}>
            <p>
                ¿Esta seguro que desea reabrir el movimiento de inventario masivo de titulo <strong>`{movimiento?.titulo}`</strong> de la fecha <strong>{dayjs(movimiento?.fecha).format('YYYY-MM-DD')}</strong>?
            </p>

            <div className="row mt-2 mb-3">
                <small className="text-danger mx-auto">Al reabrir se realizaran los movimientos inversos de los <strong>{movimiento?.productos?.length}</strong> productos presente.</small>
            </div>

            <div className="form-group">
                <label>PIN de seguridad</label>
                <input
                    type="password"
                    className="form-control"
                    id="PIN"
                    name="pin_seguridad"
                    value={pinSeguridad}
                    onChange={(e) => setPinSeguridad(e.target.value)}
                />
                <p
                    className="font-weight-bold"
                    style={{
                        color: "gray",
                        fontStyle: "italic",
                        lineHeight: "13px",
                        fontSize: "0.7rem",
                    }}
                >
                    Para continuar ingrese PIN de seguridad
                </p>
            </div>
        </Modal>

        <Modal
            show={showDeleteModal}
            blank={false}
            toggle={closeDeleteModal}
            acceptText="Eliminar movimiento masivo"
            callback={borrarMovimiento}
            saving={ejecutando}>
            <p>
                ¿Esta seguro que desea eliminar el movimiento de inventario masivo de titulo <strong>`{movimiento?.titulo}`</strong> de la fecha <strong>{dayjs(movimiento?.fecha).format('YYYY-MM-DD')}</strong>?
            </p>

            <div className="form-group">
                <label>PIN de seguridad</label>
                <input
                    type="password"
                    className="form-control"
                    id="PIN"
                    name="pin_seguridad"
                    value={pinSeguridad}
                    onChange={(e) => setPinSeguridad(e.target.value)}
                />
                <p
                    className="font-weight-bold"
                    style={{
                        color: "gray",
                        fontStyle: "italic",
                        lineHeight: "13px",
                        fontSize: "0.7rem",
                    }}
                >
                    Para continuar ingrese PIN de seguridad
                </p>
            </div>
        </Modal>

        <ImportarCsv
            show={showImportar}
            toggle={() => setShowImportar(false)}
            onExportar={() => {
                setShowImportar(false);
                setShowExportar(true);
            }}
            onImportado={() => getMovimientos(`movimientos${filtersQuery}`)}
        />

        <ExportarInventario show={showExportar} toggle={() => setShowExportar(false)} />
    </>;
}

export default InventarioMasivo;
